import { useEffect, useState } from 'react';
import { useForm, useFormState } from 'react-hook-form';
import config from '@packages/config';
import { useIntl } from 'react-intl';
import {
  Box,
  Card,
  CardContent,
  Notification,
  RadioGroup,
  Select,
  Stack,
  TextField,
  Typography,
  useDeviceType,
} from '@packages/shared';
import { type CustomerDataWithStatus, useCustomer } from '@packages/utilities';
import type {
  GTMEventGlycerinDisplayError,
  GTMEventGlycerinDisplayForm,
  GTMEventGlycerinFormSubmitClick,
  GTMEventGlycerinUnsubscribe,
} from '@packages/tracking';
import { GlycerinErrorSeverity, useTracking } from '@packages/tracking';
import type { CmsFormsFieldConfig, CmsFormsFieldOptionsConfig } from '@packages/config/src/default';

import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import type { FormSubmitResult } from '../types';
import { Errors, InputIDs } from '../types';
import { formMessages } from '../messages';
import { ActivityOverlay } from '../ActivityOverlay';
import { SuccessPage } from '../SuccessPage';
import { smallBasicContainer, styleContentTop, stylePanel } from '../styles';
import { getFormattetMessage } from '../helpers/getFormattetMessage';
import {
  frequencyMessages,
  getSuccessMessages,
  pauseMessages,
  unsubscribeMessages,
} from './helpers/messages';
import { rot47 } from '../../../utils';
import { FormFooterArea } from '../FormFooterAreaV2';
import type { NewsletterUnsubscribeFormProps } from './types';
import type { ConvertedFormUnsubNLDataType } from '../GeneralForm/helpers';
import { submitMessage } from '../GeneralForm/helpers';

export enum NewsletterUnsubscribeFormTestIds {
  main = 'newsletter-unsubscribe-container',
  header = 'newsletter-unsubscribe-header',
  form = 'newsletter-unsubscribe-form',
}
type InputID = 'email' | 'frequency' | 'pause';
type SetErrorType = `root.${InputID}` | 'root';
type FormValuesV2 = {
  [InputIDs.EMAIL]: string;
  [InputIDs.FREQUENCY]: string;
  [InputIDs.PAUSE]?: string;
};

const buildYupSchemaWithIntlFormatMessage = (intl: any) =>
  yup.object().shape({
    [InputIDs.EMAIL]: yup
      .string()
      .required(Errors.REQUIRED)
      .email(intl.formatMessage(formMessages[InputIDs.EMAIL].error)),
    [InputIDs.FREQUENCY]: yup.string().required(Errors.REQUIRED),
    [InputIDs.PAUSE]: yup.string(),
  });
const getDefaultValues = (customer: CustomerDataWithStatus | undefined, token?: string) => ({
  [InputIDs.EMAIL]: token ? rot47(token) : customer?.email,
  [InputIDs.FREQUENCY]: token ? 'unsubscribe|99' : '',
});

const convertFormDataToPostData = (
  areaKey: string,
  data: FormValuesV2,
): ConvertedFormUnsubNLDataType => {
  // radio values are strings with pattern "type|value"
  const [frequencyType, frequencyValue] =
    (data[InputIDs.FREQUENCY] && data[InputIDs.FREQUENCY].split('|')) || [];

  let typeValue = Number(frequencyValue);

  // PAUSE NEWSLETTER: If selected, set the pause value as typeValue
  if (frequencyType === 'pause') {
    typeValue = Number(data[InputIDs.PAUSE]);
  }
  return {
    emailAddress: data[InputIDs.EMAIL] || '',
    type: frequencyType,
    typeValue,
    areaKey,
  };
};

/**
 * The Newsletter unsubscribe form.
 *
 * It allows the user to unsubsribe from newsletter, change the frequency of receiving it
 * or set a pause time period.
 *
 * There can be different configurations for each tenant of what can be selected (see readme.md)
 */
const NewsletterUnsubscribeForm = ({
  headline,
  copyText,
  areaKey,
  buttonLabel,
  buttonLabelMobile,
  token,
}: NewsletterUnsubscribeFormProps) => {
  const intl = useIntl();
  const { customer } = useCustomer();
  const dispatchGTMEvent = useTracking();

  const [errorNotification, setErrorNotification] = useState({ show: false, message: '' });
  const [formSubmitResult, setFormSubmitResult] = useState<FormSubmitResult | null>(null);

  const { isMobile } = useDeviceType();
  const customButtonLabel = isMobile ? buttonLabelMobile : buttonLabel;
  const defaultLabel = customButtonLabel || intl.formatMessage(unsubscribeMessages.submit);
  const [finalButtonLabel, setFinalButtonLabel] = useState<string>(defaultLabel);

  const { register, handleSubmit, setError, control, setValue, watch } = useForm<FormValuesV2>({
    mode: 'onChange',
    defaultValues: getDefaultValues(customer, token),
    resolver: yupResolver(buildYupSchemaWithIntlFormatMessage(intl)),
  });
  const { isValid, isSubmitting, errors, isSubmitSuccessful } = useFormState({ control });
  // see documentation of "Watch in a Form" here https://react-hook-form.com/docs/useform/watch
  useEffect(() => {
    const subscription = watch(
      (value) =>
        config.forms.newsletterUnsubscribe.useDedicatedSubmitButtonTitleFor &&
        value[InputIDs.FREQUENCY] &&
        setFinalButtonLabel(
          config.forms.newsletterUnsubscribe.useDedicatedSubmitButtonTitleFor.includes(
            value[InputIDs.FREQUENCY],
          )
            ? intl.formatMessage(unsubscribeMessages.submitButtonTitle)
            : defaultLabel,
        ),
    );
    return () => subscription.unsubscribe();
  }, [defaultLabel, intl, watch]);

  const submitForm = async (data: FormValuesV2) => {
    // do stuff;
    await submitMessage<SetErrorType>({
      intl,
      isMobile,
      endpoint: 'change',
      setError,
      convertedFormUnsubNLData: convertFormDataToPostData(areaKey, data),
      onSuccess: (resultType) => {
        setFormSubmitResult(resultType as unknown as FormSubmitResult);
        dispatchGTMEvent({
          event: 'ga_event',
          eventCategory: 'User Interactions',
          eventLabel: 'newsletter',
          eventAction: 'unsubscribe',
        });
        dispatchGTMEvent<GTMEventGlycerinFormSubmitClick>({
          event: 'FormSubmitClick',
          FormSubmitClickData: {
            category: 'newsletter-unsubscribe',
            label: 'registration',
            detail: 'send',
            custom: { areaKey },
          },
        });
        if (data.email && data.frequency?.split('|')[0] === 'unsubscribe') {
          dispatchGTMEvent<GTMEventGlycerinUnsubscribe>({
            event: 'Unsubscribe',
            UnsubscribeData: {
              email: data?.email,
              custom: { areaKey },
            },
          });
        }
      },
      onError: (errMsg) => {
        setErrorNotification({ show: true, message: errMsg });
        dispatchGTMEvent<GTMEventGlycerinDisplayError>({
          event: 'DisplayError',
          DisplayErrorData: {
            category: 'newsletter-unsubscribe',
            label: 'registration',
            detail: 'error',
            errors: [
              {
                errorType: 'formValidation',
                message: errMsg,
                severity: GlycerinErrorSeverity.WARNING,
                source: 'newsletter unsubscribe form',
              },
            ],
          },
        });
      },
    });
  };

  useEffect(
    () => {
      dispatchGTMEvent<GTMEventGlycerinDisplayForm>({
        event: 'DisplayForm',
        DisplayFormData: {
          category: 'newsletter-unsubscribe',
          label: 'registration',
        },
      });
    },
    // INSPIRE-3537 - only fire once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  /**
   * Gather the basic props (that only depend on the id) for each input
   */
  const getFieldProps = (inputId: InputID) => {
    const currentLabel = intl.formatMessage(formMessages[inputId]?.label);

    return {
      ...register(inputId),
      id: inputId,
      helperText: errors[inputId]?.message,
      label: currentLabel !== (formMessages[inputId]?.label.id || '') ? currentLabel : '',
      error: !!errors[inputId],
    };
  };

  // show Success Page if form has been successfully submitted
  if (isSubmitSuccessful) {
    const msg = getSuccessMessages(formSubmitResult?.resultType);
    return (
      <SuccessPage
        headline={intl.formatMessage(msg.headline)}
        description={intl.formatMessage(msg.description)}
      />
    );
  }

  return (
    <ActivityOverlay showLoader={isSubmitting}>
      <Notification
        open={errorNotification.show}
        severity="error"
        autoHideDuration={3500}
        onClose={() => setErrorNotification({ ...errorNotification, show: false })}
      >
        {errorNotification.message}
      </Notification>
      {headline && (
        <Box sx={styleContentTop} data-testid={NewsletterUnsubscribeFormTestIds.header}>
          <Typography variant="h3" component="div" sx={{ marginBottom: 2.25 }}>
            {headline}
          </Typography>
          {copyText}
        </Box>
      )}
      <form
        noValidate
        onSubmit={handleSubmit(submitForm)}
        data-testid={NewsletterUnsubscribeFormTestIds.form}
      >
        <Box sx={stylePanel} data-testid={NewsletterUnsubscribeFormTestIds.main}>
          <Card sx={smallBasicContainer}>
            <CardContent sx={{ padding: 3 }}>
              <Stack spacing={4}>
                {config.forms.newsletterUnsubscribe.fields
                  .filter(
                    (item: CmsFormsFieldConfig) =>
                      item.formType === 'radio' && item.options && item.options?.length > 0,
                  )
                  .map((item: CmsFormsFieldConfig) => (
                    <Box key={item.id}>
                      <Box
                        sx={{
                          '& .MuiFormControl-root': {
                            // upper label
                            '&:first-of-type > label': {
                              typography: 'body2',
                              fontWeight: 'fontWeightBold',
                              color: 'text.primary',
                              marginBottom: 1,
                            },
                            // each option
                            '& label:not(:first-child)': {
                              marginTop: 1,
                              // each option label
                              '& span': { typography: 'body2' },
                            },
                          },
                        }}
                      >
                        <RadioGroup
                          initialValue=""
                          label={intl.formatMessage(formMessages[InputIDs.FREQUENCY]?.label)}
                          options={item.options!.map((option: CmsFormsFieldOptionsConfig) => ({
                            label: getFormattetMessage(frequencyMessages, option.messageKey, intl),
                            value: String(option.value), // e.g. "unsubscribe|99" or "frequency|0"
                          }))}
                          handleChange={(_e, value) => setValue(InputIDs.FREQUENCY, value)}
                          control={control}
                          controllerName={InputIDs.FREQUENCY}
                        />
                      </Box>
                      {item.additionalForm?.options &&
                        item.additionalForm.options.length > 0 &&
                        // render the subform if the selected value matches "showOnlyWhenWhatSelected"
                        // or if "showOnlyWhenWhatSelected" is not specified
                        (!item.additionalForm.showOnlyWhenWhatSelected ||
                          watch(InputIDs.FREQUENCY)?.includes(
                            item.additionalForm.showOnlyWhenWhatSelected,
                          )) && (
                          <Select
                            label={intl.formatMessage(formMessages[InputIDs.PAUSE]?.label)}
                            items={item.additionalForm.options.map(
                              (option: CmsFormsFieldOptionsConfig) => ({
                                label: getFormattetMessage(pauseMessages, option.messageKey, intl),
                                value: String(option.value),
                              }),
                            )}
                            controlProps={{ fullWidth: true }}
                            control={control}
                            controllerName={InputIDs.PAUSE}
                          />
                        )}
                    </Box>
                  ))}

                <TextField
                  {...getFieldProps(InputIDs.EMAIL)}
                  required
                  placeholder={intl.formatMessage(formMessages[InputIDs.EMAIL].placeholder)}
                  data-testid={InputIDs.EMAIL}
                  {...register}
                />
              </Stack>
            </CardContent>
          </Card>
        </Box>

        <FormFooterArea
          isValid={isValid}
          submitButton={{
            enabled: finalButtonLabel,
            disabled: intl.formatMessage(unsubscribeMessages.submitDisabled),
          }}
        />
      </form>
    </ActivityOverlay>
  );
};

// eslint-disable-next-line import/no-default-export
export default NewsletterUnsubscribeForm;
